import { GridCellParams } from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useState } from "react";

import { Artifact } from "../../../types/artifact";
import type { RankingTimeframe } from "../../../types/callRanking";
import { ALL_AGENTS } from "../../../types/callRanking";
import { ADJACENT_OFFSET, INITIAL_INDEX, NOT_FOUND_INDEX } from "../constants/numeric";
import { FindBestWorstCallsState, useFindBestWorstCallsStore } from "../stores/findBestWorstCallsStore";
import type { UseRankedCallsModeResult } from "./useRankedCallsMode";

const SCORECARD_TAB = 1;
const SUMMARY_TAB = 0;
const CHECKBOX_COLUMN_FIELD = "__check__";

interface UseExploreNavigationParams {
  artifacts: Artifact[];
  rankedCalls: UseRankedCallsModeResult;
  setFindBestWorstWizardOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UseExploreNavigationResult {
  selectedInteractionId: string | null;
  setSelectedInteractionId: React.Dispatch<React.SetStateAction<string | null>>;
  isViewerPanelOpen: boolean;
  setIsViewerPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  viewerInitialTab: number;
  setViewerInitialTab: React.Dispatch<React.SetStateAction<number>>;
  expandedAttribute: string | null;
  currentRowIndex: number;
  openInteraction: (params: GridCellParams) => void;
  handleReopenWizard: () => void;
  handleNavigatePrevious: () => void;
  handleNavigateNext: () => void;
}

interface OpenHandlerParams {
  rankedCalls: UseRankedCallsModeResult;
  setSelectedInteractionId: React.Dispatch<React.SetStateAction<string | null>>;
  setViewerInitialTab: React.Dispatch<React.SetStateAction<number>>;
  setExpandedAttribute: React.Dispatch<React.SetStateAction<string | null>>;
  setIsViewerPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const buildOpenInteraction = (deps: OpenHandlerParams) => (params: GridCellParams): void => {
  if (params.field === CHECKBOX_COLUMN_FIELD) {
    return;
  }
  const interactionId = params.row.interaction_id;
  if (!interactionId) {
    return;
  }
  const isAttrClick = params.field === "attribute_score" && deps.rankedCalls.rankedCallsParams?.attribute;
  deps.setSelectedInteractionId(interactionId);
  deps.setViewerInitialTab(isAttrClick ? SCORECARD_TAB : SUMMARY_TAB);
  deps.setExpandedAttribute(isAttrClick ? deps.rankedCalls.rankedCallsParams?.attribute ?? null : null);
  deps.setIsViewerPanelOpen(true);
};

interface ReopenHandlerParams {
  rankedCalls: UseRankedCallsModeResult;
  restoreFromParams: FindBestWorstCallsState["restoreFromParams"];
  setFindBestWorstWizardOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const buildReopenWizard = (deps: ReopenHandlerParams) => (): void => {
  if (deps.rankedCalls.rankedCallsParams) {
    deps.restoreFromParams({
      order: deps.rankedCalls.rankedCallsParams.order,
      lens: deps.rankedCalls.rankedCallsParams.lens,
      attribute: deps.rankedCalls.rankedCallsParams.attribute || null,
      timeframe: deps.rankedCalls.rankedCallsParams.timeRange as RankingTimeframe,
      agent: deps.rankedCalls.rankedCallsParams.agent || ALL_AGENTS,
      resultCount: deps.rankedCalls.rankedCallsParams.count
    });
  }
  deps.setFindBestWorstWizardOpen(true);
};

export const useExploreNavigation = ({
  artifacts, rankedCalls, setFindBestWorstWizardOpen
}: UseExploreNavigationParams): UseExploreNavigationResult => {
  const { restoreFromParams } = useFindBestWorstCallsStore();
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  const [isViewerPanelOpen, setIsViewerPanelOpen] = useState(false);
  const [viewerInitialTab, setViewerInitialTab] = useState<number>(SUMMARY_TAB);
  const [expandedAttribute, setExpandedAttribute] = useState<string | null>(null);

  const openInteraction = buildOpenInteraction({
    rankedCalls, setSelectedInteractionId, setViewerInitialTab, setExpandedAttribute, setIsViewerPanelOpen
  });

  const handleReopenWizard = buildReopenWizard({ rankedCalls, restoreFromParams, setFindBestWorstWizardOpen });

  const currentRowIndex = useMemo((): number => {
    if (!selectedInteractionId) {
      return NOT_FOUND_INDEX;
    }
    return artifacts.findIndex((artifact: Artifact) => artifact.interaction_id === selectedInteractionId);
  }, [selectedInteractionId, artifacts]);

  const handleNavigatePrevious = useCallback((): void => {
    if (currentRowIndex > INITIAL_INDEX) {
      setSelectedInteractionId(artifacts[currentRowIndex - ADJACENT_OFFSET].interaction_id);
    }
  }, [currentRowIndex, artifacts]);

  const handleNavigateNext = useCallback((): void => {
    if (currentRowIndex < artifacts.length - ADJACENT_OFFSET) {
      setSelectedInteractionId(artifacts[currentRowIndex + ADJACENT_OFFSET].interaction_id);
    }
  }, [currentRowIndex, artifacts]);

  return {
    selectedInteractionId, setSelectedInteractionId,
    isViewerPanelOpen, setIsViewerPanelOpen,
    viewerInitialTab, setViewerInitialTab,
    expandedAttribute, currentRowIndex,
    openInteraction, handleReopenWizard, handleNavigatePrevious, handleNavigateNext
  };
};
